import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as breakpoints from "../../data/breakpoints";
import * as userActions from "../../actions/userActions";
import * as favouriteActions from "../../actions/favouriteActions";
import * as followActions from "../../actions/followActions";
import * as accessActions from "../../actions/accessActions";
import * as organisationActions from "../../actions/organisationActions";
import { folderActionsAccess } from "../../utils/accessLevelCheck";
import PageContainer from "../Global/Containers/PageContainer";
import MaxWidthContainer from "../Global/Containers/MaxWidthContainer";
import MainContentContainer from "../Global/Containers/MainContentContainer";
import Comments from "../Global/Comments/Comments";
import SessionActions from "./SessionActions";
import DrillHeader from "../Drill/DrillHeader";
import DrillContent from "../Drill/DrillContent";
import SessionOverview from "./SessionOverview";
import SessionNav from "./SessionNav";
import Advert from "../Global/Adverts/Advert";
import AddToFolderModal from "../Folders/AddToFoldersModal";
import UpsellModal from "../Global/Modals/UpsellModal";
import { TCM_SQUARE_AD, NX_SQUARE_AD } from "../../data/adverts";
import staticUrl from "../../utils/staticPath";

class SessionPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeDrill: 0,
      printStyle: `compact`,
      printing: false,
      foldersModalOpen: false,
      upsellModalOpen: false,
      upsellModalFeature: `folders`,
    };
  }

  componentWillReceiveProps(nextProps) {
    // loggedInUser API is fired as part of App.js as it is used by the <Header> component
    // This means that the loggedInUser and content API's are asynchronous
    // Which is why we need these checks so that we can fire only once no matter on what order the API's return
    if (this.props.content.id !== nextProps.content.id) {
      if (nextProps.content.drills.length > 0) {
        this.props.actions.canIAccess(
          nextProps.content.drills[this.state.activeDrill].id,
          `Content`,
        );
        this.callLoggedInOnlyAPIs(nextProps);
      }
    }

    if (this.props.loggedInUser !== nextProps.loggedInUser) {
      this.callLoggedInOnlyAPIs(nextProps);
    }
  }

  userLoggedInAndContentReturned = (nextProps) => {
    if (nextProps.loggedInUser.id && nextProps.content.id) {
      return true;
    }
    return false;
  };

  callLoggedInOnlyAPIs = (nextProps) => {
    if (this.userLoggedInAndContentReturned(nextProps)) {
      this.props.actions.isFavourite(
        nextProps.content.id,
        nextProps.content.type,
      );

      const practiceAuthorIds = nextProps.content.drills.map((drill) => {
        return drill.author.id;
      });
      this.props.actions.doIFollow([
        nextProps.content.author.id,
        ...practiceAuthorIds,
      ]);
    }
  };

  handleFavouriteClick = (e) => {
    e.preventDefault();
    if (this.props.loggedInUser.computed_access) {
      // Logged in users
      this.toggleFavourite();
    } else {
      this.showUpsellModal(`favourite`);
    }
  };

  toggleFavourite = () => {
    if (this.props.favourited === true) {
      this.props.actions.removeFavourite(
        this.props.content.id,
        this.props.content.type,
      );
    } else {
      this.props.actions.addFavourite(
        this.props.content.id,
        this.props.content.type,
      );
    }
  };

  handleFollowClick = (action) => {
    const authorId = this.props.content.author.id;
    if (action === `follow`) {
      this.props.actions.followUser(authorId);
    } else {
      this.props.actions.unfollowUser(authorId);
    }
  };

  handleDrillFollowClick = (action) => {
    const authorId = this.props.content.drills[this.state.activeDrill].author
      .id;
    if (action === `follow`) {
      this.props.actions.followUser(authorId);
    } else {
      this.props.actions.unfollowUser(authorId);
    }
  };

  changeDrill = (index) => {
    let numberOfDrills = this.props.content.drills.length;
    numberOfDrills -= 1;
    let activeDrill = this.state.activeDrill;

    switch (index) {
      case `previous`:
        if (activeDrill === 0) {
          activeDrill = numberOfDrills;
        } else {
          activeDrill -= 1;
        }
        break;

      case `next`:
        if (activeDrill === numberOfDrills) {
          activeDrill = 0;
        } else {
          activeDrill += 1;
        }
        break;

      default:
        activeDrill = index;
    }

    this.setState({ activeDrill }, () => {
      this.props.actions.canIAccess(
        this.props.content.drills[this.state.activeDrill].id,
        `Content`,
      );
      this.scrollToTopOfDrill();
    });
  };

  scrollToTopOfDrill = () => {
    let anchor = document.getElementById(`scroll-anchor`);
    anchor = anchor.getBoundingClientRect().top + window.scrollY;

    // Changing the offset depending on whether main nav is on top or bottom
    let offsetTop;
    if (window.matchMedia(`(min-width: ${breakpoints.MEDIUM})`)) {
      offsetTop = 90;
    } else {
      offsetTop = 30;
    }
    TweenLite.to(window, 0.2, { scrollTo: anchor - offsetTop });
  };

  printClick = (printType) => {
    this.setState({ printing: true });
    switch (printType) {
      case `compact`:
        // Bit hacky but we need a set timeout when printing multiple to give images time to render
        this.setState({ printStyle: `compact` }, () => {
          setTimeout(this.print, 1000);
        });
        break;
      case `detailed`:
        this.setState({ printStyle: `detailed` }, () => {
          setTimeout(this.print, 1000);
        });
        break;
      case `current`:
        this.setState({ printStyle: `current` }, this.print);
      // no default
    }
  };

  print = () => {
    window.print();
    this.setState({ printing: false });
  };

  handleAddToFolderClick = (e) => {
    e.preventDefault();
    if (this.props.loggedInUser.computed_access) {
      const canAccess = folderActionsAccess(
        this.props.loggedInUser.computed_access,
      );
      if (canAccess === false) {
        // Logged in but don't have access to the feature
        e.preventDefault();
        this.showUpsellModal(`folders`);
      } else {
        this.openFoldersModal();
      }
    } else {
      this.showUpsellModal(`folders`);
    }
  };

  showUpsellModal = (upsellType) => {
    this.setState({ upsellModalOpen: true, upsellModalFeature: upsellType });
  };

  openFoldersModal = () => {
    this.setState({ foldersModalOpen: true });
  };

  closeModal = (modal) => {
    this.setState({ [modal]: false });
  };

  render() {
    const {
      loggedInUser,
      content,
      favourited,
      commentCount,
      doIFollow,
      access,
      organisation,
    } = this.props;
    const {
      activeDrill,
      printStyle,
      printing,
      foldersModalOpen,
      upsellModalOpen,
      upsellModalFeature,
    } = this.state;

    let drills;
    if (content.drills.length > 0) {
      if (
        printing === true &&
        (printStyle === `detailed` || printStyle === `compact`)
      ) {
        drills = content.drills.map(drill => (
          <DrillContent
            key={drill.id}
            content={drill}
            access={access}
            organisation={organisation}
            commentCount={commentCount}
            doIFollow={doIFollow}
            onFollowClick={this.handleDrillFollowClick}
            loggedInUser={loggedInUser}
            inSession
          />
        ));
      } else {
        drills = (
          <DrillContent
            content={content.drills[activeDrill]}
            access={access}
            organisation={organisation}
            commentCount={commentCount}
            doIFollow={doIFollow}
            onFollowClick={this.handleDrillFollowClick}
            loggedInUser={loggedInUser}
            inSession
          />
        );
      }
    }
    const logoAsset = `${staticUrl()}assets/images/svg/logos.svg#tcm`;
    return (
      <PageContainer classes={`bg-white session-page ${printStyle}`}>
        <div className="session-hero">
          <MaxWidthContainer>
            <div className="row">
              <div className="col-lg-7">
                <div className="content-header-new">
                  <svg className="print-only icon logo fill-dark icon-tcm">
                    <use xlinkHref={logoAsset} />
                  </svg>
                  <div className={`content-type content-bar ${content.type}`}>
                    <span>{content.type}</span>
                    <span className="teaser-age-range">
                      {content.tcm_age_range}
                    </span>
                  </div>
                  <h1 className="content-title big-heading">{content.title}</h1>
                  <h2 className="content-summary">{content.summary}</h2>
                  <DrillHeader
                    content={content}
                    doIFollow={doIFollow}
                    onFollowClick={this.handleFollowClick}
                    loggedInUser={loggedInUser}
                  />
                </div>

                <SessionActions
                  cmsCreated={content.cms_created}
                  favourited={favourited}
                  favouriteClick={this.handleFavouriteClick}
                  reported={content.reported}
                  reportClick={this.reportClick}
                  printClick={this.printClick}
                  folderClick={this.handleAddToFolderClick}
                  loggedInUser={loggedInUser}
                />
              </div>

              <div className="col-lg-5 col-session-overview">
                <SessionOverview
                  drills={content.drills}
                  activeDrill={activeDrill}
                  changeDrill={this.changeDrill}
                  loading={content.loading}
                />
              </div>
            </div>
          </MaxWidthContainer>
        </div>

        <MaxWidthContainer>
          <MainContentContainer>
            <div id="scroll-anchor" className="row">
              <div className="col-lg-7 col-main-content">
                {drills}

                {content.drills.length > 1 && (
                  <SessionNav changeDrill={this.changeDrill} />
                )}

                {(loggedInUser.notLoggedIn || loggedInUser.display_adverts) && (
                  <div className="advert-wrap border-radius-l bg-grey--lightest border-1 border-color-grey--light">
                    <Advert advert={TCM_SQUARE_AD} />
                    <Advert advert={NX_SQUARE_AD} />
                  </div>
                )}

                {content.drills.length > 1 && (
                  <Comments
                    contentId={content.drills[activeDrill].id}
                    contentType="content"
                    loggedInUser={loggedInUser}
                  />
                )}
              </div>
            </div>
          </MainContentContainer>
        </MaxWidthContainer>

        <AddToFolderModal
          contentId={content.id}
          contentType={content.type}
          modalIsOpen={foldersModalOpen}
          closeModal={() => this.closeModal(`foldersModalOpen`)}
        />

        <UpsellModal
          loggedInUser={loggedInUser}
          modalIsOpen={upsellModalOpen}
          upsellFeature={upsellModalFeature}
          onCloseClick={() => this.closeModal(`upsellModalOpen`)}
        />
      </PageContainer>
    );
  }
}

SessionPage.propTypes = {
  loggedInUser: PropTypes.object.isRequired,
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  favourited: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  commentCount: PropTypes.number,
  doIFollow: PropTypes.array,
  access: PropTypes.object,
  organisation: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    loggedInUser: state.loggedInUser,
    content: state.session,
    favourited: state.favourited,
    commentCount: state.comments.count,
    doIFollow: state.doIFollow,
    access: state.access,
    organisation: state.organisation,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      Object.assign(
        {},
        userActions,
        favouriteActions,
        followActions,
        accessActions,
        organisationActions,
      ),
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SessionPage);



// WEBPACK FOOTER //
// ./library/app/components/Session/SessionPage.js